import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateUserRoleScope } from "@/lib/api/roles";
import { useGroups } from "@/lib/api/group";
import { useAbility } from "@/lib/casl/ability-context";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface EditScopeDialogProps {
  userId: string;
  roleId: string;
  roleName: string;
  currentScope: { groups?: string[] };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditScopeDialog({
  userId,
  roleId,
  roleName,
  currentScope,
  open,
  onOpenChange,
}: EditScopeDialogProps) {
  const { data: groups } = useGroups(false);
  const updateScope = useUpdateUserRoleScope(userId);
  const ability = useAbility();
  const canManageAll = ability.can("manage", "all");

  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  // Sync with current scope when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedGroupIds(currentScope.groups ?? []);
    }
  }, [open, currentScope]);

  const handleSubmit = () => {
    updateScope.mutate(
      { roleId, scope: { groups: selectedGroupIds } },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Edit Group Scope — {roleName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Groups</Label>
            <p className="text-xs text-muted-foreground">
              {canManageAll
                ? "Select which groups this role is scoped to. Leave empty to remove group scoping."
                : "Select at least one group. You cannot remove all groups from scope."}
            </p>

            {selectedGroupIds.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedGroupIds.map((groupId) => {
                  const group = groups?.find((g) => g.id === groupId);
                  return (
                    <Badge key={groupId} variant="secondary" className="gap-1">
                      {group?.name || groupId}
                      <button onClick={() => toggleGroup(groupId)}>
                        <X className="size-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            <Select
              value=""
              onValueChange={(groupId) => {
                if (!selectedGroupIds.includes(groupId)) {
                  toggleGroup(groupId);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Add group..." />
              </SelectTrigger>
              <SelectContent>
                {groups
                  ?.filter((g) => !selectedGroupIds.includes(g.id))
                  .map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              updateScope.isPending ||
              (!canManageAll && selectedGroupIds.length === 0)
            }
          >
            {updateScope.isPending ? "Saving..." : "Save Scope"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
